import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import StudioSelect from "@/components/masterOrder/StudioSelect";

const kindChoices = [
  ["product", "Товар"],
  ["service", "Услуга"],
  ["menu", "Меню"],
  ["vacancy", "Вакансия"],
  ["collaboration", "Сотрудничество"],
];
const kindName = (key) => kindChoices.find(([id]) => id === key)?.[1] || key;
const questionChoices = [
  ["text", "Короткий ответ"], ["single_choice", "Один вариант"],
  ["multiple_choice", "Несколько вариантов"], ["date", "Дата"], ["quantity", "Количество"],
];
const blank = () => ({
  kind: "product", title: "", description: "", category: "", keywords: "",
  price_display: "", fulfillment_terms: "", intake_questions: [],
});
const inputClass = "w-full min-h-11 rounded-xl border border-[#826751]/25 bg-white/90 px-3 py-2.5 text-sm text-[#3B2C22] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#755F4E]";
const buttonClass = "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#826751]/25 bg-[#FFFCF8] px-4 py-2 text-sm text-[#4A382B] hover:bg-[#F3E8DA] disabled:opacity-50";
function editable(offer) {
  return { ...offer, keywords: (offer.keywords || []).join(", "),
    intake_questions: (offer.intake_questions || []).map((question) => ({
      ...question, options: [...(question.options || [])],
    })),
  };
}
function offerPayload(data) {
  return {
    kind: data.kind,
    title: data.title.trim(),
    description: data.description.trim(),
    category: data.category.trim(),
    keywords: [...new Set(data.keywords.split(",").map((s) => s.trim()).filter(Boolean))],
    price_display: data.price_display.trim(),
    fulfillment_terms: data.fulfillment_terms.trim(),
    intake_questions: (data.intake_questions || []).map((question) => ({
      ...(question.id ? { id: question.id } : {}),
      prompt: question.prompt.trim(), kind: question.kind,
      required: question.required === true,
      options: ["single_choice", "multiple_choice"].includes(question.kind)
        ? (question.options || []).map((value) => value.trim()).filter(Boolean) : [],
    })),
  };
}

// Separate from photographic portfolio and from a public paid placement.
export default function ProfessionalMapOffersWorkspace({ cardId, readOnly = false, onEditingChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const path = cardId ? "/professional-map/cards/" + encodeURIComponent(cardId) + "/offers" : null;

  // Parent page protects unsaved offer edits before leaving the main editor.
  useEffect(() => { if (!readOnly) onEditingChange?.(Boolean(editing)); }, [editing, readOnly, onEditingChange]);

  useEffect(() => {
    let current = true;
    setItems([]);
    setEditing(null);
    setForm(blank());
    setError("");
    if (!path) return () => { current = false; };
    setLoading(true);
    api.get(path).then(({ data }) => {
      if (current) setItems(Array.isArray(data) ? data : []);
    }).catch(() => {
      if (current) setError("Не удалось загрузить предложения. Попробуй обновить список.");
    }).finally(() => {
      if (current) setLoading(false);
    });
    return () => { current = false; };
  }, [path]);

  function start(item) {
    setError("");
    setDeleteTarget(null);
    setForm(item ? editable(item) : blank());
    setEditing(item?.id || "new");
  }
  function change(key, value) {
    setForm((old) => ({ ...old, [key]: value }));
  }
  function changeQuestion(index, key, value) {
    setForm((old) => ({ ...old, intake_questions: (old.intake_questions || []).map((item, i) =>
      i === index ? { ...item, [key]: value, ...(key === "kind" ? { options: [] } : {}) } : item,
    ) }));
  }
  function addQuestion() {
    setForm((old) => ({ ...old, intake_questions: [
      ...(old.intake_questions || []),
      { prompt: "", kind: "text", required: false, options: [] },
    ] }));
  }
  function removeQuestion(index) {
    setForm((old) => ({ ...old, intake_questions: (old.intake_questions || []).filter((_, i) => i !== index) }));
  }
  async function save(event) {
    event.preventDefault();
    if (!path || busy) return;
    const data = offerPayload(form);
    if (data.intake_questions.some((q) => q.prompt.length < 3 ||
      (["single_choice", "multiple_choice"].includes(q.kind) && q.options.length < 2))) {
      setError("Заполни текст каждого вопроса и добавь хотя бы два варианта для вопросов с выбором.");
      return;
    }
    if (data.title.length < 2 || data.keywords.length > 25 || data.keywords.some((w) => w.length > 80)) {
      setError("Напиши название от 2 символов и не более 25 коротких поисковых слов.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (editing === "new") {
        const result = await api.post(path, data);
        setItems((prev) => [result.data, ...prev]);
      } else {
        const result = await api.patch(path + "/" + encodeURIComponent(editing), {
          ...data, expected_revision: form.revision,
        });
        setItems((prev) => prev.map((item) => item.id === result.data.id ? result.data : item));
      }
      setEditing(null);
      setForm(blank());
    } catch (err) {
      setError("Предложение не сохранено. " + formatApiError(err) + ". Проверь список перед повтором.");
    } finally {
      setBusy(false);
    }
  }
  async function remove(item) {
    if (!path || busy) return;
    setBusy(true);
    setError("");
    try {
      await api.delete(path + "/" + encodeURIComponent(item.id), {
        params: { expected_revision: item.revision },
      });
      setItems((prev) => prev.filter((record) => record.id !== item.id));
      setDeleteTarget(null);
    } catch (err) {
      setError("Не удалось убрать предложение. " + formatApiError(err));
    } finally {
      setBusy(false);
    }
  }
  if (!cardId) return (
    <p className="text-sm text-[#806B5C]">Сначала сохрани профессиональную страницу, чтобы добавить предложения.</p>
  );
  return (
    (!readOnly || loading || error || items.length > 0) && <section className="border-t border-[#826751]/15 px-4 py-3 sm:px-7 sm:py-4"
      aria-label="Предложения" data-testid="pm-owner-offers">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl sm:text-2xl">Предложения</h2>
          <p className="mt-1 text-sm text-[#806B5C]">Товары, услуги, меню и сотрудничество отдельно от фотографий работ.</p>
          {!readOnly && <p className="mt-1 text-xs text-[#806B5C]">Предложения сохраняются как личные черновики и пока не публикуются в поиске.</p>}
        </div>
        {!readOnly && !editing && <button type="button" className={buttonClass} onClick={() => start(null)}
          data-testid="pm-offer-add"><Plus size={16}/> Добавить предложение</button>}
      </div>
      {loading && <p role="status" className="mt-3 text-sm">Загружаем предложения…</p>}
      {error && <p className="mt-3 text-sm text-red-700" role="alert">{error}</p>}
      {!readOnly && !loading && !error && !items.length && !editing && (
        <p className="mt-4 text-sm text-[#806B5C]">Предложений пока нет. Добавь первое.</p>
      )}
      {!!items.length && <div className="mt-4 space-y-2">
        {items.map((item) => <article key={item.id}
          className="rounded-xl border border-[#826751]/15 bg-[#FFFCF8]/90 p-3 sm:p-4"
          data-testid="pm-offer-row">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-[#806B5C]">{kindName(item.kind)} · Черновик{item.category ? " · " + item.category : ""}</p>
              <h3 className="mt-1 text-base font-medium break-words">{item.title}</h3>
              {!!item.description && <p className="mt-2 whitespace-pre-wrap text-sm text-[#705C4D]">{item.description}</p>}
              {!!item.price_display && <p className="mt-1 text-sm">{item.price_display}</p>}
              {!!item.fulfillment_terms && <p className="mt-1 text-xs text-[#705C4D]">{item.fulfillment_terms}</p>}
            </div>
            {!readOnly && <div className="flex gap-1">
              <button type="button" className={buttonClass + " !px-3"} disabled={busy || !!editing}
                onClick={() => start(item)} aria-label={"Редактировать " + item.title}><Pencil size={16}/></button>
              <button type="button" className={buttonClass + " !px-3"} disabled={busy || !!editing}
                onClick={() => setDeleteTarget(item)} aria-label={"Убрать " + item.title}><Trash2 size={16}/></button>
            </div>}
          </div>
        </article>)}
      </div>}
      {!readOnly && deleteTarget && <div className="mt-3 rounded-xl border border-[#826751]/25 bg-[#F4EADD] p-3">
        <p className="text-sm">Убрать «{deleteTarget.title}» из личных предложений?</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className={buttonClass} disabled={busy} onClick={() => remove(deleteTarget)}>Убрать</button>
          <button type="button" className={buttonClass} disabled={busy} onClick={() => setDeleteTarget(null)}>Отменить</button>
        </div>
      </div>}
      {!readOnly && editing && <form onSubmit={save} className="mt-4 space-y-3 rounded-xl border border-[#826751]/20 bg-[#FFFCF8] p-4"
        data-testid="pm-offer-form">
        <div className="flex justify-between gap-2">
          <h3 className="font-serif text-lg">{editing === "new" ? "Новое предложение" : "Редактировать предложение"}</h3>
          <button type="button" className={buttonClass + " !px-3"} disabled={busy}
            onClick={() => { setEditing(null); setError(""); }} aria-label="Закрыть редактор"><X size={16}/></button>
        </div>
        <div role="group" aria-labelledby="pm-offer-kind-label">
          <p className="mb-1 text-sm" id="pm-offer-kind-label">Тип предложения</p>
          <StudioSelect value={form.kind} onChange={(value) => change("kind", value)}
            options={kindChoices.map(([value, label]) => ({ value, label }))}
            testid="pm-offer-kind" />
        </div>
        <label className="block space-y-1 text-sm"><span>Название *</span>
          <input className={inputClass} required minLength={2} maxLength={140}
            value={form.title} onChange={(e) => change("title", e.target.value)} placeholder="Например, свадебные торты на заказ"/>
        </label>
        <label className="block space-y-1 text-sm"><span>Описание</span>
          <textarea className={inputClass} rows={3} maxLength={3000}
            value={form.description} onChange={(e) => change("description", e.target.value)}/>
        </label>
        <label className="block space-y-1 text-sm"><span>Категория</span>
          <input className={inputClass} maxLength={120} value={form.category}
            onChange={(e) => change("category", e.target.value)} placeholder="Например, торты"/>
        </label>
        <label className="block space-y-1 text-sm"><span>Поисковые слова через запятую</span>
          <input className={inputClass} value={form.keywords}
            onChange={(e) => change("keywords", e.target.value)}
            placeholder="свадебный, шоколадный, доставка"/>
        </label>
        <label className="block space-y-1 text-sm"><span>Стоимость (текст)</span>
          <input className={inputClass} maxLength={120} value={form.price_display}
            onChange={(e) => change("price_display", e.target.value)}
            placeholder="от 30 € / по запросу"/>
        </label>
        <label className="block space-y-1 text-sm"><span>Получение, доставка или условия</span>
          <textarea className={inputClass} rows={2} maxLength={500}
            value={form.fulfillment_terms} onChange={(e) => change("fulfillment_terms", e.target.value)}/>
        </label>
        <section className="rounded-xl border border-[#826751]/15 bg-[#F6EFE5] p-3 sm:p-4"
          aria-label="Свой опросник клиента" data-testid="pm-offer-intake-builder">
          <h4 className="font-serif text-lg">Вопросы для клиента</h4>
          <p className="mt-1 text-xs leading-5 text-[#705C4D]">
            Необязательно. Добавь только те вопросы, которые нужны для обсуждения этого предложения.
            Прямое обращение без опросника остаётся доступно.
          </p>
          <p className="mt-2 text-xs leading-5 text-[#705C4D]">
            Здесь можно указать нежелательные ингредиенты, но не нужно сообщать диагнозы, аллергии или причины медицинских ограничений.
            Anokina Studio не принимает медицинские анкеты.
          </p>
          <p className="mt-1 text-xs text-[#806B5C]">Сейчас сохраняется только шаблон вопросов. Клиентские ответы пока не принимаются.</p>
          <div className="mt-3 space-y-3">
            {(form.intake_questions || []).map((question, index) => (
              <div key={question.id || index} className="space-y-2 rounded-xl border border-[#826751]/20 bg-white p-3"
                data-testid="pm-offer-intake-question">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">Вопрос {index + 1}</p>
                  <button type="button" className={buttonClass + " !px-3"} disabled={busy}
                    onClick={() => removeQuestion(index)} aria-label={"Убрать вопрос " + (index + 1)}>
                    <Trash2 size={15} />
                  </button>
                </div>
                <label className="block space-y-1 text-sm"><span>Текст вопроса</span>
                  <input className={inputClass} maxLength={220} value={question.prompt}
                    onChange={(e) => changeQuestion(index, "prompt", e.target.value)}
                    placeholder="Например, на какую дату нужен торт?"/>
                </label>
                <div role="group" aria-label={"Формат ответа для вопроса " + (index + 1)}>
                  <p className="mb-1 text-sm">Формат ответа</p>
                  <StudioSelect value={question.kind} onChange={(value) => changeQuestion(index, "kind", value)}
                    options={questionChoices.map(([value, label]) => ({value, label}))}
                    testid={"pm-intake-kind-" + index} />
                </div>
                {["single_choice", "multiple_choice"].includes(question.kind) && (
                  <label className="block space-y-1 text-sm"><span>Варианты через запятую</span>
                    <textarea className={inputClass} rows={2}
                      value={(question.options || []).join(", ")}
                      onChange={(e) => changeQuestion(index, "options", e.target.value.split(",").map((v) => v.trim()))}
                      placeholder="Классика, современный, минимализм"/>
                  </label>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={question.required === true}
                    onChange={(e) => changeQuestion(index, "required", e.target.checked)}/>
                  Обязательный вопрос
                </label>
              </div>
            ))}
          </div>
          <button type="button" className={buttonClass + " mt-3"} disabled={busy || (form.intake_questions || []).length >= 30}
            onClick={addQuestion} data-testid="pm-offer-intake-add"><Plus size={16}/> Добавить вопрос</button>
        </section>
        <div className="flex flex-wrap gap-2">
          <button type="submit" className={buttonClass + " bg-[#F4EADD]"} disabled={busy}>
            {busy ? "Сохраняем…" : "Сохранить предложение"}
          </button>
          <button type="button" className={buttonClass} disabled={busy}
            onClick={() => { setEditing(null); setError(""); }}>Отменить</button>
        </div>
      </form>}
    </section>
  );
}
